import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Input } from './ui/input';
import { Button } from './ui/button';

/**
 * Reusable machinery multi-picker.
 *
 * Used on the inventory dialogs to tag which machines a part is applicable
 * to. Grouped by location so C-1A vs. C-1B (or two "Chiller"s in different
 * plants) are unambiguous.
 *
 * Props:
 *   value    — string[] machinery UUIDs
 *   onChange — setter
 *   disabled — bool
 */
const MachineryPicker = ({ value = [], onChange, disabled = false }) => {
  const [machinery, setMachinery] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        const [locRes, mRes] = await Promise.all([
          axios.get(`${API}/locations`),
          axios.get(`${API}/machinery`),
        ]);
        if (aborted) return;
        setLocations(locRes.data || []);
        setMachinery(mRes.data || []);
      } catch {
        // non-fatal — user can still submit form without tagging equipment
      } finally {
        if (!aborted) setLoading(false);
      }
    })();
    return () => { aborted = true; };
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const locNameById = useMemo(() => {
    const m = new Map();
    locations.forEach((l) => m.set(l.id, l.name));
    return m;
  }, [locations]);

  const machineryById = useMemo(() => {
    const m = new Map();
    machinery.forEach((mach) => m.set(mach.id, mach));
    return m;
  }, [machinery]);

  const selectedIds = new Set(value);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return machinery
      .filter((m) => {
        if (selectedIds.has(m.id)) return false;
        const comp = (m.component || '').toLowerCase();
        const loc = (locNameById.get(m.plant_location_id) || 'Unassigned').toLowerCase();
        const mfr = (m.manufacturer || '').toLowerCase();
        return comp.includes(q) || loc.includes(q) || mfr.includes(q);
      })
      .slice(0, 12);
  }, [query, machinery, selectedIds, locNameById]);

  const addMachinery = (m) => {
    if (selectedIds.has(m.id)) return;
    onChange([...value, m.id]);
    setQuery('');
    setShowDropdown(false);
  };

  const removeMachinery = (id) => {
    onChange(value.filter((v) => v !== id));
  };

  const clearAll = () => onChange([]);

  return (
    <div className="space-y-2" ref={containerRef} data-testid="machinery-picker">
      {/* Selected chips */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="machinery-picker-chips">
          {value.map((id) => {
            const m = machineryById.get(id);
            const label = m
              ? `${locNameById.get(m.plant_location_id) || 'Unassigned'} → ${m.component}`
              : id;
            return (
              <span
                key={id}
                data-testid={`machinery-picker-chip-${id}`}
                className="inline-flex items-center gap-1 bg-slate-900 text-white text-xs rounded-full pl-2.5 pr-1 py-0.5"
              >
                {label}
                <button
                  type="button"
                  onClick={() => removeMachinery(id)}
                  disabled={disabled}
                  aria-label="Remove"
                  className="h-4 w-4 rounded-full hover:bg-white/20 text-white/80 hover:text-white leading-none"
                  data-testid={`machinery-picker-remove-${id}`}
                >
                  ×
                </button>
              </span>
            );
          })}
          {value.length > 1 && (
            <button
              type="button"
              onClick={clearAll}
              disabled={disabled}
              className="text-xs text-slate-500 hover:text-red-600 underline ml-1"
              data-testid="machinery-picker-clear-all"
            >
              Clear all
            </button>
          )}
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <Input
          data-testid="machinery-picker-search"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setShowDropdown(true); }}
          onFocus={() => setShowDropdown(true)}
          placeholder={loading ? 'Loading equipment…' : 'Type to add equipment (component, location, or manufacturer)'}
          disabled={disabled || loading}
        />
        {showDropdown && query.trim() && (
          <div
            className="absolute z-30 left-0 right-0 mt-1 bg-white border rounded-md shadow-lg max-h-64 overflow-y-auto"
            data-testid="machinery-picker-dropdown"
          >
            {matches.length === 0 ? (
              <div className="px-3 py-3 text-sm text-slate-500 italic">
                No matching equipment.
              </div>
            ) : matches.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => addMachinery(m)}
                data-testid={`machinery-picker-option-${m.id}`}
                className="w-full text-left px-3 py-2 hover:bg-slate-100 border-b last:border-b-0"
              >
                <div className="text-sm font-medium">{m.component}</div>
                <div className="text-xs text-slate-500">
                  {locNameById.get(m.plant_location_id) || 'Unassigned'}
                  {m.manufacturer ? ` · ${m.manufacturer}` : ''}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      <p className="text-xs text-slate-500">
        Optional. Tag every piece of equipment this part fits so techs can find it fast (e.g. C-1A and C-1B for sister compressors).
        To bulk-tag a whole parts list onto a sister unit, use &quot;Copy from…&quot; on the equipment page&apos;s Applicable Parts card.
      </p>
    </div>
  );
};

export default MachineryPicker;
